import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  ItemSettingsResponseSchema,
  type ItemSettings,
  type SettingsLockedBy,
  type UpdateItemSettingsParams,
} from "@forgepoint/shared";
import { captureException } from "@/lib/analytics";
import { fetchApi } from "@/lib/api";

const UpdateItemSettingsResponseSchema = ItemSettingsResponseSchema.pick({
  settings: true,
});

interface ItemSettingsState {
  settings: ItemSettings | null;
  lockedBy: SettingsLockedBy;
  isLoading: boolean;
  isSaving: boolean;
  hasFetched: boolean;
  error: string | null;
  fetchSettings: (options?: { force?: boolean }) => Promise<void>;
  updateSettings: (
    params: UpdateItemSettingsParams,
  ) => Promise<ItemSettings | undefined>;
}

let latestFetchRequestId = 0;

/** Zustand store for organization-level item settings. */
export const useItemSettingsStore = create<ItemSettingsState>()(
  immer((set, get) => ({
    settings: null,
    lockedBy: {},
    isLoading: false,
    isSaving: false,
    hasFetched: false,
    error: null,

    /**
     * Fetches the settings and ERP locks, applying only the latest request's response.
     * @param options - `force` bypasses the in-flight dedupe so a refetch always runs
     * @returns Resolves once the fetch settles
     */
    fetchSettings: async (options) => {
      if (get().isLoading && !options?.force) {
        return;
      }
      const requestId = ++latestFetchRequestId;
      set({ isLoading: true, error: null });
      try {
        const response = await fetchApi("item-settings", "GET");
        if (requestId !== latestFetchRequestId) {
          return;
        }
        if (!response.ok) {
          throw new Error(response.error ?? "Failed to fetch item settings");
        }
        const parsed = ItemSettingsResponseSchema.parse(response.data);
        set({
          settings: parsed.settings,
          lockedBy: parsed.lockedBy,
          isLoading: false,
          hasFetched: true,
        });
      } catch (error) {
        if (requestId !== latestFetchRequestId) {
          return;
        }
        const normalizedError =
          error instanceof Error ? error : new Error("Unknown error");
        captureException(normalizedError);
        set({
          error: normalizedError.message,
          isLoading: false,
          hasFetched: true,
        });
      }
    },

    /**
     * Saves the given item settings; omitted keys keep their stored values.
     * @param params - The custom fields schema and/or advanced settings to save
     * @returns The saved settings, or undefined when the save fails
     */
    updateSettings: async (params) => {
      set({ isSaving: true, error: null });
      try {
        const response = await fetchApi("item-settings", "POST", {
          body: params,
        });
        if (!response.ok) {
          throw new Error(response.error ?? "Failed to update item settings");
        }
        const parsed = UpdateItemSettingsResponseSchema.parse(response.data);
        if (!parsed.settings) {
          throw new Error("Item settings response was empty");
        }
        set({
          settings: parsed.settings,
          isSaving: false,
          hasFetched: true,
        });
        return parsed.settings;
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Unknown error");
        captureException(normalizedError);
        set({ error: normalizedError.message, isSaving: false });
        return undefined;
      }
    },
  })),
);
